import {PublicKey, Transaction, ComputeBudgetProgram} from '@solana/web3.js';
import {
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import {createMemoInstruction} from '@solana/spl-memo';
import {SolanaTransactionData} from 'src/types';
import {getSolanaTokenProgramId} from 'src/utils/getSolanaTokenProgramId';
// KEEP THIS IN SYNC WITH lib/crypto_standalone_site/wallet_connect/src/inner/solanaHelpers.ts

const _buildTokenTransferTransaction = (
  sourceAddress: PublicKey,
  destinationAddress: PublicKey,
  destinationTokenAccountAddress: PublicKey | null,
  token: PublicKey,
  // amount is in token minor units (USDC and USDP minor unit is 6)
  amount: bigint,
  memoMessage: string,
  latestBlockhash: string,
  feePayer: PublicKey,
  tokenProgramId: PublicKey,
): Transaction => {
  const sourceAssociatedTokenAccountAddress = getAssociatedTokenAddressSync(
    token,
    sourceAddress,
    false,
    tokenProgramId,
  );

  const destinationAssociatedTokenAccountAddress =
    getAssociatedTokenAddressSync(
      token,
      destinationAddress,
      false,
      tokenProgramId,
    );

  if (destinationTokenAccountAddress !== null) {
    if (
      destinationTokenAccountAddress?.toBase58() !==
      destinationAssociatedTokenAccountAddress?.toBase58()
    ) {
      throw Error(
        `[BuildAndSignTokenTransferTransaction] wrong associated token account address for selected asset ${destinationTokenAccountAddress?.toBase58()} ${destinationAssociatedTokenAccountAddress?.toBase58()} ${token.toBase58()} ${destinationAddress.toBase58()}`,
      );
    }
  }

  const transaction = new Transaction();
  // make sure to recheck the compute limit if we change the transaction instructions
  transaction.add(
    ComputeBudgetProgram.setComputeUnitLimit({
      units: 100000,
    }),
  );
  // set a pretty high priority fee (2 lamports per compute unit)
  // overall fee should end up being 0.000025 SOL
  transaction.add(
    ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 1000000,
    }),
  );
  // create associated token account if recipient does not have one
  if (destinationTokenAccountAddress === null) {
    transaction.add(
      createAssociatedTokenAccountInstruction(
        sourceAddress,
        destinationAssociatedTokenAccountAddress,
        destinationAddress,
        token,
        tokenProgramId,
      ),
    );
  }
  const decimals = 6;
  transaction.add(
    createTransferCheckedInstruction(
      sourceAssociatedTokenAccountAddress,
      token,
      destinationAssociatedTokenAccountAddress,
      sourceAddress,
      amount,
      decimals,
      undefined,
      tokenProgramId,
    ),
  );
  transaction.add(createMemoInstruction(memoMessage, [sourceAddress]));
  transaction.recentBlockhash = latestBlockhash;
  transaction.feePayer = feePayer;
  return transaction;
};

export const getSourceTokenAccountAddress = (
  currencyContractAddress: string,
  sourceAddress: string,
  currency: string,
): PublicKey => {
  const sourceAddressPublicKey = new PublicKey(sourceAddress);
  const token = new PublicKey(currencyContractAddress);
  const programId = getSolanaTokenProgramId(currency);
  return getAssociatedTokenAddressSync(
    token,
    sourceAddressPublicKey,
    false,
    programId,
  );
};

export const buildTokenTransferTransaction = (
  solanaTransactionData: SolanaTransactionData,
): Transaction => {
  const {
    destinationAddress,
    destinationTokenAccountAddress,
    currencyContractAddress,
    amount,
    memoMessage,
    latestBlockhash,
    walletAddress,
    currency,
  } = solanaTransactionData;
  const walletPublicKey = new PublicKey(walletAddress);
  const token = new PublicKey(currencyContractAddress);
  const tokenProgram = getSolanaTokenProgramId(currency);
  const transferTransaction = _buildTokenTransferTransaction(
    walletPublicKey,
    new PublicKey(destinationAddress),
    destinationTokenAccountAddress === null
      ? null
      : new PublicKey(destinationTokenAccountAddress),
    token,
    amount,
    memoMessage,
    latestBlockhash,
    // TODO: switch to our fee payer address when we support gasless txn
    walletPublicKey,
    tokenProgram,
  );
  return transferTransaction;
};
